import { Box, LinearProgress, Paper } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { useCreateUnitMutation } from "@/mutations/unit.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { ListOwners } from "@/queries/owners.ts";
import UnitForm, { type UnitFormData } from "@/routes/admin/-forms/UnitForm.tsx";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const ownerData: { data: ListOwners[] } = useSuspenseQuery(qof.owners.list()).data;
    const navigate = useNavigate();

    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const createUnitMutation = useCreateUnitMutation();

    const handleAddNewUnit = (data: UnitFormData) => {
        createUnitMutation.mutate({
            attributes: data,
            type: "calendar",
        });
        setShowSuccessToast(true);
        navigate({ to: "/admin/units", search: { rentalId: `${data.rentalId}` } });
    };

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title="Add a Unit to this rental program."
                    instructions={[
                        'Enter required data and click "Add New Unit" button.',
                        "Each Unit should be entered into the database only once.",
                        'If changes need to be made to the Unit info later, you should go to "All Owners & Units" to make the changes there.',
                    ]}
                    onBack={() => navigate({ to: "/admin/units" })}
                    backText="← Back to All Units"
                />
                <UnitForm owners={ownerData} submitHandler={handleAddNewUnit} />
            </Paper>

            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Unit Added Successfully!"
                message="The new unit has been added to the rental program database."
            />
        </Box>
    );
};

export const Route = createFileRoute("/admin/units/create")({
    component: Root,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.owners.list());
    },
});
